import { h, createContext } from 'preact';
import { useEffect, useContext, useState, useCallback } from 'preact/compat';

import { useCookies } from '../../hooks';
import { api } from '../../services';
import { useAppContext, useGtmContext } from '../index';
import { parseLocationData, parseSessionData, parseGeneralData, parseBootstrapGeneralData } from './userContext.helper';
import { readGeneralApplicationDataScript } from '../AppContext/appContext.utils';
import { signOut as signOutRequest } from './userContext.auth';

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const INITIAL_USER_DATA = {
    firstName:      '',
    customerId:     '',
    customerEmail:  '',
    sessionFormKey: '',
};

const INITIAL_CART_DATA = {
    cartId:   null,
    qtyItems: 0,
};

// ─────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────

const UserContext = createContext(null);

export const UserContextProvider = ({ children }) => {
    const [userData, setUserData]   = useState(INITIAL_USER_DATA);
    const [cartData, setCartData]   = useState(INITIAL_CART_DATA);
    // Seeded synchronously from the payload the server inlines in the head, so
    // the layout flags it carries (nadEnable) are known on the first render
    // instead of arriving with /v1/data. The fetch below overwrites this with
    // the full payload — same store config, so no second layout change.
    const [generalData, setGeneralData] = useState(
        () => parseBootstrapGeneralData(readGeneralApplicationDataScript())
    );

    const [isSignedIn, setIsSignedIn]                       = useCookies('user_signedIn', false);
    const [acceptedTermUse, setAcceptedTermUse]              = useCookies('term_use', false);
    const [acceptedExamsUseTerms, setAcceptedExamsUseTerms] = useCookies('exams_use_terms', false);

    const {
        setFullPageLoader,
        setLocations,
        setRecaptchaData,
        setSocialLoginConfig,
        setNoticesData,
        storeLocation,
        setStoreLocation,
    } = useAppContext();

    const { addEventToGTM } = useGtmContext();

    // ── Auth ─────────────────────────────────

    const signOut = useCallback(
        () => signOutRequest(setFullPageLoader),
        [setFullPageLoader]
    );

    // ── Data fetching ─────────────────────────

    const applySessionData = useCallback((session) => {
        if (!session.customerName) {
            setIsSignedIn(false);
            setUserData(prev => ({ ...prev, sessionFormKey: session.formKey }));
            return;
        }

        setUserData(parseSessionData(session));
        setIsSignedIn(!!session.customerName);
    }, [setIsSignedIn]);

    const applyLocations = useCallback((stores) => {
        setLocations(stores.map(parseLocationData));
    }, [setLocations]);

    /**
     * The server session is the source of truth for the active region
     * (PEC-509). When the STORE_LOCATION cookie diverges from the store
     * the server resolved at boot (expired store cookie, external
     * ?___store= link), realign the cookie and reload so every
     * region-dependent view (product units, prices, cart) renders the
     * server region. After the reload the two match, so this runs at
     * most once per desync. Desyncs that appear AFTER boot (another tab
     * switching the session store) are recovered reactively when the
     * backend refuses the cart mutation — see hooks/RegionSync.
     */
    const applyCurrentStore = useCallback((currentStore) => {
        if (!currentStore?.id) return;

        if (Number(storeLocation.id) !== Number(currentStore.id)) {
            setStoreLocation({
                id:   currentStore.id,
                name: currentStore.name,
            });
            window.location.reload();
        }
    }, [storeLocation.id, setStoreLocation]);

    const fetchData = useCallback(async () => {
        try {
            const { data } = await api.get('/v1/data');

            applySessionData(data.session);
            applyLocations(data.stores);
            applyCurrentStore(data.currentStore);

            setGeneralData(parseGeneralData(data.data, data.offer, data.ctaPopUp, data.affiliation));
            setSocialLoginConfig(data.socialLogin);
            setRecaptchaData(data.recaptcha);
            setCartData(data.miniCart);
            setNoticesData(data.notices);
        } catch (err) {
            console.error('Error while fetching user data:', err);
        }
    }, [
        applySessionData,
        applyLocations,
        applyCurrentStore,
        setNoticesData,
        setRecaptchaData,
        setSocialLoginConfig,
    ]);

    // ── Effects ───────────────────────────────

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!isSignedIn || !userData.customerId) return;

        addEventToGTM({
            event:     'client_id',
            client_id: { id: userData.customerId },
        });
    }, [isSignedIn, userData.customerId, addEventToGTM]);

    // ─────────────────────────────────────────
    // Context value
    // ─────────────────────────────────────────

    const value = [
    {
        ...userData,
        isSignedIn,
        setIsSignedIn,
    },
    {
        signOut,
    },
    {
        cartData,
        setCartData,
        acceptedTermUse,
        setAcceptedTermUse,
        acceptedExamsUseTerms,
        setAcceptedExamsUseTerms,
    },
    {
        ...generalData,
    },
];

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

const useUserContext = () => useContext(UserContext);
export default useUserContext;
